'use client';

import { Color } from '@diritto/common/style/color';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import {
  DesktopContentWidth,
  DesktopNavigationWidth,
  GNBHeight,
  isPC,
  MobileGNBMenuHeight,
  MutableHeaderNavBarMobileHeight,
} from 'constants/layout';
import { useAuthState } from 'contexts/AuthProvider';
import { usePathname } from 'next/navigation';
import React, { ReactNode, useMemo } from 'react';
import { Divider } from 'ui-components/common/Divider';
import { SafeAreaBottomStyles } from 'ui-components/layout/styles';
import { DesktopGlobalMenu } from 'ui-components/navigations/DesktopGlobalMenu';
import { GlobalNavigationBar } from 'ui-components/navigations/GlobalNavigationBar';
import { BackButton, HomeButton } from 'ui-components/navigations/HeaderButtons';
import { HeaderNavigationBar, HeaderNavigationBarProps } from 'ui-components/navigations/HeaderNavigationBar';
import { getMatchedMenu } from 'utils/navigation';

interface HeaderLayoutProps extends HeaderNavigationBarProps {
  children: ReactNode;
  hasTab?: boolean;
  showHeaderInApp?: boolean; // todo refac 앱인데 헤더를 웹에서 제어해야하는 경우 때문에 생긴 props
  noBottomPadding?: boolean;
  isApp?: boolean;
}

/* PC에서는 GNB가 함께 보이고, 모바일에서는 Header만 보이는 레이아웃 */
export const MutableHeaderFixedLayout = ({
  children,
  leftButtons,
  hasTab,
  showHeaderInApp = false,
  noBottomPadding = false,
  isApp = false,
  ...rest
}: HeaderLayoutProps) => {
  const pathname = usePathname();
  const { partner } = useAuthState();
  const defaultLeftButtons = useMemo(() => [<BackButton key="back" />, <HomeButton key="home" />], []);
  const currentMenu = useMemo(() => getMatchedMenu(pathname ?? ''), [pathname]);
  const showNavHeader = showHeaderInApp || !isApp;

  return (
    <StyledFixedWrapper>
      <StyledGnbContainer>
        <GlobalNavigationBar selectedMenu={currentMenu} showPartnersLogo={!!partner?.verifiedAt} />
        <Divider />
      </StyledGnbContainer>
      <StyledNavWrapper noBottomPadding={noBottomPadding}>
        <DesktopGlobalMenu selectedMenu={currentMenu} />
        <StyledMain>
          {showNavHeader && (
            <StyledHeaderContainer>
              <HeaderNavigationBar leftButtons={leftButtons || defaultLeftButtons} {...rest} />
              <NavigationBarDivider hasTab={hasTab} />
            </StyledHeaderContainer>
          )}

          <StyledChildrenWrapper showNavHeader={showNavHeader}>{children}</StyledChildrenWrapper>
        </StyledMain>
      </StyledNavWrapper>
    </StyledFixedWrapper>
  );
};

const StyledFixedWrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
`;

const StyledGnbContainer = styled.nav`
  display: none;
  position: relative;
  background-color: ${Color.White};
  width: 100%;

  @media (${isPC}) {
    display: block;
  }
`;

const StyledNavWrapper = styled.div(
  ({ noBottomPadding }: { noBottomPadding: boolean }) => css`
    width: 100%;
    height: 100%;
    margin: 0 auto;

    ${SafeAreaBottomStyles(noBottomPadding ? 0 : MobileGNBMenuHeight)};

    @media (${isPC}) {
      display: flex;
      padding-bottom: 0;
      width: ${DesktopNavigationWidth}px;
      height: calc(100% - ${GNBHeight}px);
    }
  `,
);

const StyledChildrenWrapper = styled.div(
  ({ showNavHeader }: { showNavHeader: boolean }) => css`
    width: 100%;
    height: calc(100% - ${showNavHeader ? MutableHeaderNavBarMobileHeight : 0}px);
  `,
);

const StyledMain = styled.main`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  position: relative;

  @media (${isPC}) {
    width: ${DesktopContentWidth}px;
    border-left: 1px solid ${Color.Gray300};
    border-right: 1px solid ${Color.Gray300};
  }
`;

const StyledHeaderContainer = styled.div`
  display: block;
  position: relative;
  background-color: ${Color.White};
  width: 100%;
`;

const NavigationBarDivider = styled(Divider)(
  ({ hasTab }: { hasTab?: boolean }) => css`
    display: ${hasTab ? 'none' : 'initial'};

    @media (${isPC}) {
      display: initial;
    }
  `,
);
